import { Space, Tooltip } from 'antd';
import moment, { ISO_8601 } from 'moment';
import React, { FC } from 'react';
import styled from 'styled-components';
import { TaskPreview } from 'api/notifications';
import { StatusGroup } from 'api/tasks';
import { ReactComponent as FormInstaceIconSvg } from 'assets/svgs/form-instance-notification-icon.svg';
import { DueDate } from 'components/atoms/due-date';
import { SquadAvatarList } from 'components/atoms/squad-avatar-list';
import { Tag } from 'components/atoms/tag';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { usePriorityField, useStatusField } from 'data/fields/hooks';
import { useTranslation } from 'hooks/use-translation';
import { Header } from './components';
import styles from './group.module.css';

interface Props {
  task: TaskPreview;
}

const FormInstanceIcon = styled(FormInstaceIconSvg)`
  margin-right: var(--spacing-04);
`;
const Status = styled(StatusIndicator)`
  margin-right: var(--spacing-04);
`;

const Priority = styled(Tag)`
  cursor: inherit;
`;

export const TaskHeader: FC<Props> = ({
  task: {
    name,
    project: { name: projectName },
    projectPhase,
    assignee,
    status,
    priority,
    dueDate,
    progress,
    formInstanceId,
  },
  children,
}) => {
  const priorityTitle = useTranslation('priority');
  const dueDateTitle = useTranslation('due_date');
  const assigneeTitle = useTranslation('assignees');
  const assigneesCleaned = assignee ?? { users: [], teams: [] };
  const renderAssignees =
    (assigneesCleaned.users ?? []).length +
      (assigneesCleaned.teams ?? []).length >
    0;
  const status_field = useStatusField();
  const priority_field = usePriorityField();
  const status_option = status_field.metaData.options.find(
    (x) => x.value === status,
  )!;
  const priority_option = priority_field!.metaData!.options?.find(
    (x) => x.value === priority,
  );

  return (
    <Header className={styles.header}>
      <Header.Primary>
        {formInstanceId ? (
          <FormInstanceIcon />
        ) : (
          <Status status={status_option} progress={progress} />
        )}
        <Header.PrimaryBlock>
          <div title={name} className={`${styles.title} ${styles.ellipsis}`}>
            {name}
          </div>
          <div
            className={`${styles.subtitle} ${styles.ellipsis}`}
          >{`${projectName} ${
            projectPhase?.name != null ? `/ ${projectPhase.name}` : ''
          }`}</div>
        </Header.PrimaryBlock>
      </Header.Primary>
      <Header.Secondary>
        <Space size={16}>
          {renderAssignees && (
            <Tooltip title={assigneeTitle}>
              <SquadAvatarList squad={assignee || { users: [], teams: [] }} />
            </Tooltip>
          )}
          {priority_option != null && (
            <Tooltip title={priorityTitle}>
              <Priority color={priority_option.color}>
                {priority_option.label}
              </Priority>
            </Tooltip>
          )}
          {dueDate != null && (
            <Tooltip
              title={`${dueDateTitle}: ${moment(dueDate, ISO_8601).format(
                'LL',
              )}`}
            >
              <span>
                <DueDate
                  className={styles.due}
                  timestamp={moment(dueDate, ISO_8601)}
                  completed={status_option.group === StatusGroup.Completed}
                />
              </span>
            </Tooltip>
          )}
        </Space>
      </Header.Secondary>
      <Header.Hanger>{children}</Header.Hanger>
    </Header>
  );
};
